import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { createContext, useContext, useRef } from 'react';

import {
  ENDPOINT_PRICE_AND_STOCK,
  ENDPOINT_ROOT,
  MAX_RETRY_DELAY,
  MAX_RETRY_LIMIT,
} from '@/common/constants';
import axiosAuth from '@/common/interceptors/axios.AuthInterceptor';

const StockContext = createContext(null);

export const StockContextProvider = ({ children, isLoggedIn }) => {
  const stock = useRef(new Map());
  const retryNb = useRef(1);
  const callDelayIndice = 2000;

  const onResetStock = () => {
    stock.current = new Map();
  };

  /**
   *
   * @param  {array} items - array of items with product inside
   * @param {function} successCallback - success callback
   * @param {function} errorCallback - error callback
   * @returns {Map} stock - called stocks in a map dictionary
   */
  const onGetSocks = ({ items, successCallback, errorCallback }) => {
    if (!isLoggedIn) {
      return false;
    }
    const promises = [];
    // get only items with no stock and sku
    items
      .filter(
        (item) =>
          _.isEmpty(item.product) ||
          _.isEmpty(item.product?.price_and_stock) ||
          _.isEmpty(item.product?.price_and_stock?.stocks),
      )
      .forEach((item) => {
        if (
          (item.calls === undefined || item.calls < MAX_RETRY_LIMIT) &&
          !!item.sku
        ) {
          const promise = axiosAuth.post(
            `${ENDPOINT_ROOT}/${ENDPOINT_PRICE_AND_STOCK}`,
            { sku: item.sku },
          );
          promises.push(promise);
          promise.then((response) => {
            // Response status :
            // - 200 contains all
            // - 206 partial content
            // - 204 no content
            const { data } = response;
            stock.current.set(item.sku, {
              ...item,
              product: {
                ...item.product,
                price_and_stock: {
                  ...item.product?.price_and_stock,
                  ...data,
                },
              },
              calls: item.calls ? item.calls + 1 : 1,
              response: response.status,
            });
          });
        }
      });

    // check if all promises are finished
    Promise.all(promises)
      .then(() => {
        // To prevent success callback execution if no call has been fired
        if (promises.length > 0) {
          const noStockTemp = [];
          stock.current.forEach((value) => {
            if (
              _.isEmpty(value.product?.price_and_stock) &&
              _.isEmpty(value.product?.price_and_stock?.stocks) &&
              value.calls < MAX_RETRY_LIMIT
            ) {
              noStockTemp.push(value);
            }
          });
          // execute given success callback
          if (noStockTemp.length > 0) {
            retryNb.current += 1;
            setTimeout(
              () =>
                onGetSocks({
                  items: [...noStockTemp],
                  successCallback,
                  errorCallback,
                }),
              Math.min(callDelayIndice * 2 ** retryNb.current, MAX_RETRY_DELAY),
            );
          } else {
            // reset retry Nb
            retryNb.current = 1;
            successCallback(stock.current);
          }
        }
      })
      .catch((error) => {
        // execute given error callback
        errorCallback(error);
      });
    // Return missing stocks
    return stock.current;
  };

  return (
    <StockContext.Provider value={{ onGetSocks, onResetStock }}>
      {children}
    </StockContext.Provider>
  );
};

StockContextProvider.defaultProps = {
  isLoggedIn: false,
};

StockContextProvider.propTypes = {
  isLoggedIn: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const useStockContext = () => useContext(StockContext);
